import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import {
  showUndoToast,
  showTaskLinkToast,
  registerPendingWrite,
  clearPendingWrite,
  UNDO_WINDOW_MS,
} from "../../lib/undoStack";
import { getCurrentAccessToken, keepaliveSupabaseRequest } from "../../lib/keepaliveRest";
import { Task } from "../../types";

/**
 * Returns a `deleteTask(id)` function that optimistically removes the task
 * from the query cache, then commits the DB delete after a 3-second undo
 * window (matching the pre-migration behaviour). If the user presses
 * Undo, the task is restored and the timer is cancelled. If the page is
 * unloaded mid-window the delete is flushed via a keepalive request.
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return {
    deleteTask: async (id: string): Promise<boolean> => {
      const currentTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];
      const task = currentTasks.find((t) => t.id === id);
      if (!task) return false;

      // Optimistic removal — reflected instantly in every consumer of useTasks.
      queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
        (prev ?? []).filter((t) => t.id !== id)
      );

      // Capture the token synchronously before the async work begins so the
      // pagehide flush can use it without an async call.
      const accessToken = await getCurrentAccessToken();
      const pendingKey = `task-delete-${id}-${Date.now()}`;

      let undone = false;

      const deleteTimer = setTimeout(async () => {
        clearPendingWrite(pendingKey);
        if (undone) return;

        const { error } = await supabase.from("tasks").delete().eq("id", id);

        if (error) {
          // Restore the task if the deferred delete fails.
          queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
            [...(prev ?? []), task].sort(
              (a, b) => a.order_position - b.order_position
            )
          );
          Sentry.captureException(error, {
            extra: { mutation: "useDeleteTask", taskId: id },
          });
          showTaskLinkToast({
            prefix: "Failed to delete ",
            label: `"${task.title}"`,
            labelSuffix: ". Please try again.",
            type: "error",
            onLabelClick: () =>
              navigate({
                to: "/task/$taskId",
                params: { taskId: id },
                search: (prev: Record<string, unknown>) => prev,
              }),
          });
          return;
        }

        Sentry.addBreadcrumb({
          category: "task",
          message: "Task deleted",
          data: { taskId: id },
          level: "info",
        });

        queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      }, UNDO_WINDOW_MS);

      // Ensure the delete fires even if the tab is closed mid-window.
      if (accessToken) {
        registerPendingWrite(pendingKey, {
          timer: deleteTimer,
          flush: () => {
            keepaliveSupabaseRequest(
              `tasks?id=eq.${encodeURIComponent(id)}`,
              { method: "DELETE" },
              accessToken
            );
          },
        });
      }

      showUndoToast({
        label: `"${task.title}" deleted`,
        onUndo: () => {
          undone = true;
          clearTimeout(deleteTimer);
          clearPendingWrite(pendingKey);
          queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
            [...(prev ?? []), task].sort(
              (a, b) => a.order_position - b.order_position
            )
          );
        },
      });

      return true;
    },
  };
}
